import axios from '../../api/config/axios';

const state = {
    user: null,
    email: null,
    password: null,
};

/** Evita várias chamadas GET /user em paralelo (corrida que gerava logout após 401). */
let checkAuthPromise = null;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Extrai usuário e token do corpo típico de POST /login (Laravel / APIs custom).
 */
function extractUserAndTokenFromLoginResponse(response) {
    const root = response?.data;
    if (!root) return { user: null, token: null };

    const payload = root.data !== undefined ? root.data : root;
    const inner = payload?.data !== undefined ? payload.data : payload;

    let user =
        inner?.user ??
        payload?.user ??
        (typeof inner === 'object' && inner && (inner.id || inner.uuid) && !inner.token ? inner : null);

    if (!user && typeof payload === 'object' && payload && (payload.id || payload.uuid) && payload.email) {
        user = payload;
    }

    const token =
        inner?.token ??
        inner?.access_token ??
        payload?.token ??
        payload?.access_token ??
        root?.token ??
        null;

    return { user, token: typeof token === 'string' && token.trim() ? token.trim() : null };
}

function normalizeUserFromUserEndpoint(response) {
    const data = response.data?.data ?? response.data;
    return data?.user ?? data;
}

const getters = {
    isAuthenticated: state => !!state.user,
    isConfirmatedEmail: state => {
        if (!state.user || typeof state.user !== 'object') return false;
        const u = state.user;
        return !!(u.email_verified_at ?? u.user?.email_verified_at);
    },
    isCompleteRegistration: state => {
        if (!state.user || typeof state.user !== 'object') return false;
        const u = state.user;
        return !!(u.complete_registration_at ?? u.user?.complete_registration_at);
    },
    StateUser: state => state.user,
};

const actions = {
    async Register({ commit }, credentials) {
        try {
            const response = await axios.post('/api/v1/register', credentials);
            commit('setUser', response.data);
        } catch (error) {
            console.error('Registration error:', error);
            throw error;
        }
    },

    /**
     * Fluxo recomendado Sanctum SPA: garantir cookie CSRF antes do POST de login.
     * Login por senha usa sessão em cookie; remove token Bearer legado (ex.: Google) para não conflitar.
     */
    async LogIn({ commit, dispatch }, credentials) {
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('auth_token');
        }

        await axios.get('/sanctum/csrf-cookie');

        const loginResponse = await axios.post('/api/v1/login', credentials);
        const { user: userFromLogin, token } = extractUserAndTokenFromLoginResponse(loginResponse);

        if (token && typeof localStorage !== 'undefined') {
            localStorage.setItem('auth_token', token);
        }

        const hasUserFromLogin = !!(userFromLogin && typeof userFromLogin === 'object');
        if (hasUserFromLogin) {
            commit('setUser', userFromLogin);
        }

        // Hidrata pelo GET /user com retries maiores após login.
        // Em alguns cenários (logout -> login rápido), o backend pode demorar
        // alguns instantes para refletir a nova sessão em /user.
        try {
            const user = await dispatch('checkAuth', {
                retries: [0, 300, 900, 1800],
                logoutOnFail: !hasUserFromLogin,
            });
            if (!user) {
                throw new Error('Usuário não retornado por /user após login.');
            }
            return user;
        } catch (error) {
            // Fallback seguro: se o login já retornou usuário válido, não derruba
            // a sessão local por uma falha transitória de /user.
            if (hasUserFromLogin) {
                console.warn('Falha transitória ao hidratar /user após login. Usando payload do login.', error);
                return userFromLogin;
            }
            throw error;
        }
    },

    async LogOut({ commit }) {
        try {
            await axios.post('/api/v1/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            commit('logout');
        }
    },

    async ResendEmailVerification() {
        try {
            await axios.post('/api/v1/email/verification-notification');
        } catch (error) {
            console.error('Logout error:', error);
        }
    },

    /**
     * Valida sessão/token. Single-flight + retries curtos para atraso de cookie/sessão no servidor.
     */
    async checkAuth({ commit }, options = {}) {
        if (checkAuthPromise) {
            return checkAuthPromise;
        }

        checkAuthPromise = (async () => {
            const retries = Array.isArray(options.retries) && options.retries.length
                ? options.retries
                : [0, 150, 400];
            const logoutOnFail = options.logoutOnFail !== false;
            let lastError = null;

            for (const delay of retries) {
                if (delay > 0) await sleep(delay);
                try {
                    const response = await axios.get('/api/v1/user');
                    const user = normalizeUserFromUserEndpoint(response);
                    if (user && typeof user === 'object') {
                        commit('setUser', user);
                        return user;
                    }
                } catch (error) {
                    lastError = error;
                    if (!error.response || error.response.status !== 401) {
                        break;
                    }
                }
            }

            if (logoutOnFail) {
                commit('logout');
            }
            if (lastError) throw lastError;
            throw new Error('Não autenticado');
        })();

        try {
            return await checkAuthPromise;
        } finally {
            checkAuthPromise = null;
        }
    },

    async forgotPassword({ commit }, email) {
        try {
            await axios.post('/api/v1/forgot-password', { email });
            return true;
        } catch (error) {
            console.error('Forgot password error:', error);
            throw error;
        }
    },

    async resetPassword({ commit }, data) {
        try {
            await axios.post('/api/v1/reset-password', {
                token: data.token,
                email: data.email,
                password: data.password,
                password_confirmation: data.password_confirmation
            });
            return true;
        } catch (error) {
            console.error('Reset password error:', error);
            throw error;
        }
    },
};

const mutations = {
    setUser(state, user) {
        state.user = user;
    },
    logout(state) {
        state.user = null;
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('auth_token');
        }
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
